import React, { useState, useCallback, useEffect } from 'react';

import { reportError } from '@this/lib/bugsnag';
import type SearchQuery from '../../../../../domain/search_query';
import type SearchQueryItem from '../../../../../domain/search_query_item';
import type SearchResultItem from '../../../../../domain/search_result_item';
import type SelectRepository from '../../../../../domain/select_repository';
import type SelectStore from '../../../../../domain/select_store';
import type HotelList from '../../../../../domain/hotel/hotel_list';
import type FlightList from '../../../../../domain/flight/flight_list';
import TransitList from '../../../../../domain/transit/transit_list';
import ListAreaShortDistanceContainer from './ListAreaShortDistanceContainer';

export interface ListAreaShortDistanceProps {
  // Select.props
  query: SearchQuery;
  store: SelectStore;
  repository: SelectRepository;
  serviceId: number;

  // ListAreaShortDistance.props
  icon: string;
  listWrapper: TransitList | HotelList | FlightList | null;
  type: string | undefined;
  subtype: number | undefined;
  show: boolean;
  hotelPriceLimit: number | null;
  queryItem?: SearchQueryItem;
  resultItem?: SearchResultItem;
  direction?: string;
  showSearchBox: boolean;
  toggleShowSearchBox: () => void;
}

const ListAreaShortDistance: React.FC<ListAreaShortDistanceProps> = props => {
  const [transportSegment, setTransportSegment] = useState<number | undefined>(props.listWrapper?.segment);
  const [isOpenFlightCondition, setIsOpenFlightCondition] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    setTransportSegment(props.listWrapper?.segment);
  }, [props.listWrapper?.segment]);

  const handleTransportSegmentClick = useCallback(
    (i: number) => () => {
      if (props.listWrapper) {
        props.listWrapper.segment = i;
        setTransportSegment(i);
      }
    },
    [props.listWrapper]
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!props.query) return;

      try {
        props.query.validationPromise();
        if (!props.query || !props.queryItem || !props.resultItem) return;

        window.history.replaceState(null, '', `${location.pathname}${props.query.getQueryString()}`);
        props.resultItem.setLoading(true);
        props.repository.startPartialSearch(props.queryItem);
        // submitErrorsの型
      } catch (submitErrors) {
        if (submitErrors && typeof submitErrors === 'object') {
          const errorMessages = Object.values(submitErrors) as string[];
          setErrors(errorMessages);
        } else {
          setErrors(['検索条件にエラーがあります']);
        }
      }
    },
    [props.query, props.queryItem, props.resultItem, props.repository]
  );

  const setItemLoading = useCallback(
    (loading: boolean) => {
      if (props.resultItem) {
        props.resultItem.setLoadingWithSelectedLoading(loading);
      }
    },
    [props.resultItem]
  );

  const handleFlightConditionSubmit = useCallback(
    (
      filteredUpgradeSeat: boolean,
      carrierIds: string[],
      changeablePrices: string[],
      showConnectingAir: boolean
    ) => {
      if (props.queryItem) {
        props.queryItem.setFilteredUpgradeSeat(filteredUpgradeSeat);
        props.queryItem.setCarrierIds(carrierIds);
        props.queryItem.setChangeablePrices(changeablePrices);
        props.queryItem.setShowConnectingAir(showConnectingAir);
      }
      if (props.listWrapper && props.listWrapper instanceof TransitList) {
        props.listWrapper.handleFilteredUpgradeSeat(filteredUpgradeSeat);
        props.listWrapper.handleAirlinesChange(carrierIds);
        props.listWrapper.handleChangeablePriceChange(changeablePrices);
        props.listWrapper.handleShowConnectingAir(showConnectingAir);
      }
      setIsOpenFlightCondition(false);
      app.render(); // TODO: observerにしたら消せる
    },
    [props.queryItem, props.listWrapper]
  );

  const openFlightCondition = useCallback(() => {
    setIsOpenFlightCondition(true);
  }, []);

  const closeFlightCondition = useCallback(() => {
    setIsOpenFlightCondition(false);
  }, []);

  try {
    return (
      <ListAreaShortDistanceContainer
        query={props.query}
        store={props.store}
        repository={props.repository}
        serviceId={props.serviceId}
        icon={props.icon}
        listWrapper={props.listWrapper}
        type={props.type}
        subtype={props.subtype}
        show={props.show}
        hotelPriceLimit={props.hotelPriceLimit}
        queryItem={props.queryItem}
        resultItem={props.resultItem}
        direction={props.direction}
        showSearchBox={props.showSearchBox}
        toggleShowSearchBox={props.toggleShowSearchBox}
        transportSegment={transportSegment}
        isOpenFlightCondition={isOpenFlightCondition}
        errors={errors}
        handleTransportSegmentClick={handleTransportSegmentClick}
        handleSubmit={handleSubmit}
        setItemLoading={setItemLoading}
        handleFlightConditionSubmit={handleFlightConditionSubmit}
        openFlightCondition={openFlightCondition}
        closeFlightCondition={closeFlightCondition}
      />
    );
  } catch (e) {
    reportError(e);
    return null;
  }
};

export default ListAreaShortDistance;
